import { Button, Card, CardContent, cn } from '@h2hspark/ui';
import * as Lucide from 'lucide-react';
import * as React from 'react';

import { CaseStudiesSection, TestimonialsSection } from './collection-sections-panel';
import { ContentLink } from './content-link-panel';
import { EventsPreviewSection } from './events-preview-panel';
import { FormSection } from './form-section-panel';
import { RichText } from './rich-text-panel';
import { Band, SectionLeadIn } from './section-band-panel';
import { list, sectionType, text, type ContentData, type PageSection } from './types';

/**
 * A page's sections, rendered by a switch on `_type` (`CMS-010`).
 *
 * **Adding a page is content; adding a *kind of* section is code.** That boundary is what keeps
 * this a finite switch rather than an interpreter, and it is the reason the palette lives in
 * `content-types.ts` where a reviewer can see it next to the renderer that consumes it.
 *
 * An unknown `_type` renders **nothing and does not throw**. A page published with a section this
 * build does not know about is still a real page: taking it down because one band is unfamiliar
 * would let a deploy destroy content, and throwing would take the whole page with it.
 */

export interface SectionsProps {
  readonly sections: ReadonlyArray<PageSection>;
}

export function Sections({ sections }: SectionsProps): React.ReactElement {
  return (
    <>
      {sections.map((section, index) => (
        <Section
          key={index}
          section={section}
          followsHero={index > 0 && sectionType(sections[index - 1] as PageSection) === 'hero'}
        />
      ))}
    </>
  );
}

function Section({
  section,
  followsHero,
}: {
  readonly section: PageSection;
  readonly followsHero: boolean;
}): React.ReactElement | null {
  switch (sectionType(section)) {
    case 'hero':
      return <Hero section={section} />;
    case 'logoStrip':
      return <LogoStrip section={section} />;
    case 'howItWorks':
      return <Steps section={section} ordered />;
    // **Not `Cards`.** The design's feature band is a gradient panel split in two, not a row of
    // white cards — see `FeatureBand`. `valueProps` and `pillars` are card rows and still are.
    case 'featureBand':
      return <FeatureBand section={section} />;
    case 'valueProps':
      return <Cards section={section} field="props" />;
    case 'pillars':
      return <Cards section={section} field="pillars" />;
    case 'statBand':
      return <StatBand section={section} />;
    case 'prose':
      return <Prose section={section} followsHero={followsHero} />;
    case 'ctaBanner':
      return <CallToAction section={section} />;
    // **Not the same band as `ctaBanner`.** In the design the organizer sell is a translucent
    // panel — `rgba(255,255,255,.08)` at `--radius-xl` — sitting *inside* the purple, not a
    // second solid slab beneath it. Rendered as one, the page ended on three consecutive blocks
    // of the same purple with no edge between them: the closing call to action, this, and the
    // footer. Same colour, three times, reads as one very tall footer.
    case 'organizerSellBand':
      return <OrganizerSellBand section={section} />;
    // The two collection sections (`CMS-015`). They are the only sections whose content is not on
    // the section: they read their own published entries at render time, so they live in their own
    // file with the query in it, and a collection with nothing published renders nothing at all.
    case 'testimonials':
      return <TestimonialsSection section={section} />;
    case 'caseStudies':
      return <CaseStudiesSection section={section} />;
    // The one section whose content is not content at all: it reads live events from the events
    // domain at request time. `content-types.ts` calls it out as the deliberate exception.
    case 'eventsPreview':
      return <EventsPreviewSection section={section} />;
    // The third fetching section (`CMS-016`), and the first that writes. It holds a form's slug
    // and the form holds its own fields, so adding a question is content — which is the whole
    // reason `/contact` is a page somebody publishes rather than a screen somebody builds.
    case 'formEmbed':
      return <FormSection section={section} />;
    default:
      // Unknown or retired. Silence is deliberate — see the header.
      return null;
  }
}

/**
 * The hero — one column, or two when the section carries a product preview.
 *
 * ## Why there are two shapes
 *
 * `Web App.dc.html`'s landing hero is a `1fr 1fr` grid at 1200px: the words on the left, and on
 * the right a card showing **the product doing its job** — a match, the reason for it, and the
 * control that acts on it. Beneath the actions, three initials and a count of members.
 *
 * We shipped the left column alone, centred, and the client said three times that the page did
 * not look like the design. It was not the wording — that had been corrected. It was that the
 * design sells the product by showing it and ours only described it.
 *
 * Every other page's hero has no preview and still wants the centred single column, so the shape
 * follows the content: `previewName` present means the two-column hero, absent means the one that
 * was here before. Nothing else on the site had to change.
 */
function Hero({ section }: { readonly section: ContentData }): React.ReactElement {
  const headline = text(section, 'headline');
  const href = text(section, 'primaryHref');
  const label = text(section, 'primaryLabel');
  const image = text(section, 'image');
  const hasPreview = text(section, 'previewName') !== '';

  const eyebrow =
    text(section, 'eyebrow') === '' ? null : (
      <p className="h2h-eyebrow mb-3">{text(section, 'eyebrow')}</p>
    );

  // `clamp(40px, 4.4vw, 60px)` in the design. Tailwind's steps land either side of that, so the
  // clamp is written out — a headline that is the page's whole argument is worth the arbitrary
  // value.
  const title = (
    <h1
      className={cn(
        'text-[clamp(2.25rem,4.4vw,3.75rem)] font-bold leading-[1.03] tracking-tight',
        hasPreview ? '' : 'mx-auto max-w-3xl',
      )}
    >
      {headline}
    </h1>
  );

  const body =
    text(section, 'body') === '' ? null : (
      <p
        className={cn(
          'mt-5 text-lg text-muted-foreground',
          hasPreview ? 'max-w-[520px]' : 'mx-auto max-w-2xl',
        )}
      >
        {text(section, 'body')}
      </p>
    );

  const actions =
    label === '' && text(section, 'secondaryLabel') === '' ? null : (
      <div
        className={cn(
          'mt-8 flex flex-col gap-3 sm:flex-row',
          hasPreview ? 'items-start' : 'items-center justify-center',
        )}
      >
        {/*
          **The shape is not the layout's business.** `hasPreview` chooses between the two-column
          hero and the centred one, and it used to choose the button's shape as well — so
          `/for-organizers`, whose hero has no preview card, drew its *Book a demo* and *Sign up* as
          12px rectangles while every CTA above and below them was a pill. §1 and §2 draw the same
          spark pill on both heroes (`--shadow-accent` included), and the design system's own
          readme makes the pill the default shape for a button. Parity pass, 2026-09-12.
        */}
        {label !== '' && href !== '' && (
          <Button asChild size="lg" className="rounded-pill bg-spark shadow-accent">
            <ContentLink href={href}>{label}</ContentLink>
          </Button>
        )}
        {text(section, 'secondaryLabel') !== '' && text(section, 'secondaryHref') !== '' && (
          <Button asChild size="lg" variant="outline" className="rounded-pill">
            <ContentLink href={text(section, 'secondaryHref')}>
              {text(section, 'secondaryLabel')}
            </ContentLink>
          </Button>
        )}
      </div>
    );

  if (!hasPreview) {
    return (
      <Band className="text-center">
        {eyebrow}
        {title}
        {body}
        {actions}
        {image !== '' && (
          <img src={image} alt="" className="mx-auto mt-10 w-full max-w-3xl rounded-xl" />
        )}
      </Band>
    );
  }

  return (
    <Band>
      <div className="grid items-center gap-14 lg:grid-cols-2">
        <div>
          {eyebrow}
          {title}
          {body}
          {actions}
          <HeroSocialProof section={section} />
        </div>
        <HeroPreviewCard section={section} />
      </div>
    </Band>
  );
}

/**
 * Three initials and a count, beneath the actions.
 *
 * **Initials, never photographs.** The design draws three avatar circles; filling them with
 * pictures of people would mean either inventing members or using real ones on a public page, and
 * neither is a thing to do quietly for decoration.
 */
function HeroSocialProof({
  section,
}: {
  readonly section: ContentData;
}): React.ReactElement | null {
  const label = text(section, 'socialProofLabel');
  const avatars = list(section, 'socialProofInitials');
  if (label === '' && avatars.length === 0) return null;

  const tints = [
    'bg-brand-200 text-brand-700',
    'bg-fuchsia-100 text-brand-700',
    'bg-brand-100 text-brand-700',
  ];

  return (
    <div className="mt-8 flex items-center gap-5">
      {avatars.length > 0 && (
        <div className="flex" aria-hidden>
          {avatars.map((avatar, index) => (
            <span
              key={index}
              className={cn(
                'inline-flex h-10 w-10 items-center justify-center rounded-pill font-brand text-[13px] font-bold ring-2 ring-background',
                tints[index % tints.length],
                index > 0 ? '-ml-3' : '',
              )}
            >
              {text(avatar, 'initials')}
            </span>
          ))}
        </div>
      )}
      {label !== '' && <p className="text-sm text-muted-foreground">{label}</p>}
    </div>
  );
}

/**
 * The product preview: a gradient-bordered card holding one match.
 *
 * The design's border is a 3px `--spark-gradient` frame around a `--surface-card` panel at 26px
 * radius — done here as a padded gradient wrapper rather than `border-image`, which cannot round
 * corners reliably across browsers.
 *
 * **Nothing in this card is interactive.** It is a picture of the product on a marketing page, and
 * a Save to meet button that did nothing would be worse than one that is plainly a sample — so it
 * renders as a styled `div`, not a `button`, and is hidden from assistive technology behind a
 * label that says what it is.
 */
function HeroPreviewCard({ section }: { readonly section: ContentData }): React.ReactElement {
  const why = text(section, 'previewWhy');
  const action = text(section, 'previewAction');

  return (
    <div
      // `0 40px 90px rgba(44,22,74,.28)` — the prototype's own shadow, and the thing I mistook
      // for a background wash behind the card. It is not a wash; it is this, a large soft
      // purple-tinted shadow spreading onto the ivory. `shadow-lg` was far too tight to read as
      // anything.
      //
      // The float is the prototype's too — `floatUp 8s ease-in-out infinite`, ±12px — and it is
      // behind `motion-safe` so a visitor who asked for less motion gets a card that sits still.
      className="rounded-2xl bg-spark p-[3px] shadow-[0_40px_90px_rgba(44,22,74,0.28)] motion-safe:animate-float"
      role="img"
      aria-label={`Example: ${text(section, 'previewHeading')} — ${text(section, 'previewName')}`}
    >
      <div className="rounded-[26px] bg-card p-6" aria-hidden>
        <div className="flex items-center gap-2">
          <Lucide.Sparkles className="h-[18px] w-[18px] text-brand-accent" />
          <span className="font-brand text-sm font-bold text-foreground">
            {text(section, 'previewHeading')}
          </span>
        </div>

        <div className="mt-3 flex flex-col gap-3">
          <div className="flex items-center gap-3 rounded-lg p-3.5">
            <span className="inline-flex h-12 w-12 flex-none items-center justify-center rounded-pill bg-brand-200 font-brand text-base font-bold text-brand-700">
              {text(section, 'previewInitials')}
            </span>
            <div className="flex-1">
              <div className="font-brand text-md font-bold text-foreground">
                {text(section, 'previewName')}
              </div>
              <div className="text-xs text-muted-foreground">{text(section, 'previewRole')}</div>
            </div>
            {text(section, 'previewScore') !== '' && (
              <span className="font-brand text-sm font-bold text-brand">
                {text(section, 'previewScore')}
              </span>
            )}
          </div>

          {why !== '' && (
            <div className="rounded-md bg-brand-50 px-3.5 py-3 text-sm leading-snug text-foreground">
              <span className="font-semibold">{text(section, 'previewWhyLabel')}</span> {why}
            </div>
          )}

          <PreviewAction section={section} fallback={action} />
        </div>
      </div>
    </div>
  );
}

/** How each authored `tone` is drawn. Roles, resolved to tokens here rather than authored. */
const PREVIEW_ACTION_TONES: Readonly<Record<string, string>> = {
  spark: 'bg-spark text-white',
  brand: 'bg-brand text-white',
  done: 'border border-success bg-success-subtle text-success',
};

/**
 * The preview card's action pill — one state, or the design's three rotating in place.
 *
 * ## Why the states are stacked rather than swapped in JavaScript
 *
 * `Web App.dc.html` draws three absolutely-positioned spans sharing one 42px stage, each running
 * the same `cycle3` with a stagger. That is a **CSS** animation and it stays one: a timer in React
 * would re-render the marketing hero every two and a half seconds forever, on every visitor's
 * machine, to move a decoration.
 *
 * The stage is sized from the tallest state rather than fixed at the prototype's 42px, so a label
 * an editor makes longer cannot clip — the prototype hard-codes a height because its three strings
 * are known.
 *
 * ## Motion, and who gets none
 *
 * Behind `motion-safe:`, like the card's float. A visitor who asked for reduced motion gets the
 * **first** state, still and fully opaque, rather than a pill that flickers or an empty stage —
 * which is why the stagger and the animation are applied together and the first child's resting
 * opacity is 1.
 *
 * Nothing here is interactive, and the whole card is already `aria-hidden` behind the `role="img"`
 * label its parent carries — so a reader who is not looking at it is told what the card is once,
 * rather than read three labels for a control that does not exist.
 */
function PreviewAction({
  section,
  fallback,
}: {
  readonly section: ContentData;
  readonly fallback: string;
}): React.ReactElement | null {
  const states = list(section, 'previewActions');

  if (states.length === 0) {
    if (fallback === '') return null;
    return (
      <div className="rounded-pill bg-spark py-3 text-center font-brand text-sm font-bold text-white">
        {fallback}
      </div>
    );
  }

  // A single authored state is a still pill, not a one-frame animation.
  const cycles = states.length > 1;
  // The prototype's own 2.5s between three states on a 7.5s cycle: the period divided by however
  // many states there are, so two or four rotate at the same pace rather than at the wrong one.
  const step = 7.5 / states.length;

  return (
    <div className="relative grid">
      {states.map((state, index) => {
        const tone = PREVIEW_ACTION_TONES[text(state, 'tone')] ?? PREVIEW_ACTION_TONES.spark;
        return (
          <div
            key={index}
            // Every state occupies the same grid cell, so the stage is as tall as the tallest of
            // them and no state can push the card's layout around as it appears.
            style={
              cycles
                ? { gridArea: '1 / 1', animationDelay: `${String(index * step)}s` }
                : { gridArea: '1 / 1' }
            }
            className={cn(
              'flex items-center justify-center gap-2 rounded-pill py-3 font-brand text-sm font-bold',
              tone,
              // Reduced motion, or a single state: the first one rests visible and the rest are
              // never drawn at all.
              cycles && index > 0 ? 'motion-reduce:hidden' : '',
              cycles ? 'motion-safe:animate-cycle' : '',
              cycles && index > 0 ? 'motion-safe:opacity-0' : '',
            )}
          >
            <PreviewActionGlyph name={text(state, 'icon')} />
            {text(state, 'label')}
          </div>
        );
      })}
    </div>
  );
}

/** A pill state's glyph. Absent or unknown draws nothing rather than a gap. */
function PreviewActionGlyph({ name }: { readonly name: string }): React.ReactElement | null {
  if (name === '') return null;
  const icons = Lucide as unknown as Record<string, React.ComponentType<{ className?: string }>>;
  const Icon = icons[name];
  if (Icon === undefined) return null;
  return <Icon className="h-4 w-4 shrink-0" />;
}

function LogoStrip({ section }: { readonly section: ContentData }): React.ReactElement {
  const logos = list(section, 'logos');
  if (logos.length === 0) return <></>;
  // One row: the label sits at the left in small uppercase, the names run beside it. It was
  // centred over a second row, which reads as a section of its own rather than as a caption on a
  // list — and at 1200px wide it left a band of empty space where the design has none.
  return (
    <div className="border-b bg-card">
      <div className="mx-auto flex w-full max-w-site flex-col gap-4 px-6 py-6 sm:flex-row sm:items-center sm:gap-8 sm:px-10">
        {text(section, 'heading') !== '' && (
          <p className="font-brand text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
            {text(section, 'heading')}
          </p>
        )}
        <ul className="flex flex-wrap items-center gap-x-10 gap-y-4">
          {logos.map((logo, index) => (
            <li key={index}>
              {text(logo, 'image') !== '' ? (
                // The name is the alt text: a logo with no accessible name is a gap in the page.
                <img src={text(logo, 'image')} alt={text(logo, 'name')} className="h-8 w-auto" />
              ) : (
                <span className="font-brand text-lg font-extrabold text-muted-foreground">
                  {text(logo, 'name')}
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * How it works — a centred lead-in over a row of cards.
 *
 * The design draws each step as a **card**: white panel, rounded, its own icon tile, a "STEP 1"
 * label in the accent, then the title and body, all left-aligned. What was here was three bare
 * columns of centred text under a bare heading — the same words in none of the same shapes, which
 * is most of why the page kept reading as "not the design" after the copy had been corrected.
 *
 * The icon tiles alternate purple and fuchsia, as they do in the design. Alternating by index
 * rather than by an authored colour on purpose: the rhythm belongs to the band, and an editor
 * adding a fourth step should not have to know which tint comes next.
 */
function Steps({
  section,
  ordered,
}: {
  readonly section: ContentData;
  readonly ordered: boolean;
}): React.ReactElement {
  const steps = list(section, 'steps');
  if (steps.length === 0) return <></>;
  const ListTag = ordered ? 'ol' : 'ul';
  return (
    <Band anchor={text(section, 'anchor')}>
      <SectionLeadIn
        eyebrow={text(section, 'eyebrow')}
        heading={text(section, 'heading')}
        intro={text(section, 'intro')}
      />
      {/* An ordered list because these are steps: the order is the meaning, and a screen reader
          should say "1 of 3" rather than leaving the sequence to the visual numbering. */}
      <ListTag className="mt-10 grid gap-6 text-left sm:grid-cols-3">
        {steps.map((step, index) => (
          <li key={index} className="rounded-xl bg-card p-7 shadow-sm ring-1 ring-border/60">
            {ordered && (
              <span
                className={cn(
                  'mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg',
                  index % 2 === 1 ? 'bg-fuchsia-50 text-brand-accent' : 'bg-brand-50 text-brand',
                )}
              >
                <StepIcon name={text(step, 'icon')} position={index + 1} />
              </span>
            )}
            {text(step, 'label') !== '' && (
              <p className="font-brand text-[11px] font-bold uppercase tracking-[0.06em] text-brand-accent">
                {text(step, 'label')}
              </p>
            )}
            <h3 className="mt-1 text-xl font-semibold">{text(step, 'title')}</h3>
            <p className="mt-2 text-md text-muted-foreground">{text(step, 'body')}</p>
          </li>
        ))}
      </ListTag>
    </Band>
  );
}

/**
 * A step's icon, or its number.
 *
 * Resolved from a Lucide name the editor types, the same indirection the console uses for content
 * type icons — so a new icon is content rather than a code change. An unknown name falls back to
 * the position rather than to nothing: a blank circle looks like a rendering failure.
 */
function StepIcon({
  name,
  position,
}: {
  readonly name: string;
  readonly position: number;
}): React.ReactElement {
  const icons = Lucide as unknown as Record<string, React.ComponentType<{ className?: string }>>;
  const Icon = name === '' ? undefined : icons[name];
  if (Icon === undefined) return <>{position}</>;
  return <Icon className="h-5 w-5" aria-hidden />;
}

function Cards({
  section,
  field,
}: {
  readonly section: ContentData;
  readonly field: string;
}): React.ReactElement {
  const items = list(section, field);
  if (items.length === 0) return <></>;
  return (
    <Band tint="sunken" anchor={text(section, 'anchor')}>
      <SectionLeadIn
        eyebrow={text(section, 'eyebrow')}
        heading={text(section, 'heading')}
        intro={text(section, 'intro')}
      />
      <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item, index) => (
          <li key={index}>
            <Card className="h-full">
              <CardContent className="pt-6">
                {/*
                  **The icon the editor authored** (`CMS-018` AC-1). `featureBand` has carried an
                  `icon` field since the palette was written and the card never read it, so the
                  prototype's three tinted glyphs — sparkles, qr-code, bar-chart — were authored
                  in the seed, stored in the entry, served by the API and then dropped on the
                  floor by the one component that could have drawn them. A field nothing renders
                  is worse than a missing field: it looks done from every side except the page.
                */}
                <CardIcon name={text(item, 'icon')} />
                <h3 className="text-base font-semibold">{text(item, 'title')}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{text(item, 'body')}</p>
              </CardContent>
            </Card>
          </li>
        ))}
      </ul>
    </Band>
  );
}

/**
 * A card's icon, resolved from the Lucide name an editor types.
 *
 * The same indirection `StepIcon` uses, and it must stay the same one: two ways to spell an icon
 * name in one renderer is how "Sparkles" works in a how-it-works step and silently does nothing in
 * a feature card. Unlike a step, a card has no number to fall back to — an unknown or empty name
 * renders **no tile at all** rather than an empty coloured square, which would read as an image
 * that failed to load.
 */

/**
 * The feature band — a gradient panel, copy on the left, features stacked on the right.
 *
 * ## What this replaced, and why it mattered more than it looks
 *
 * It was rendered by `Cards`: a centred heading on the page background with three white cards in
 * a row beneath it. The design is a single **`--spark-gradient` panel** at `--radius-2xl`, split
 * `1fr 1fr`, with an eyebrow, a white headline, a paragraph and a white pill button on the left,
 * and on the right three translucent cards stacked vertically.
 *
 * Every word was already right. Nothing about the words was the problem: this band is the loudest
 * thing on the page in the design — the one full-colour block between two pale sections — and
 * ours was the palest. A page can have all the correct sentences in the wrong order of emphasis
 * and read as a different page, which is what kept happening here.
 *
 * The cards are `rgba(255,255,255,.14)` on the gradient rather than a token, because they are a
 * tint *of that gradient* rather than a surface the theme owns — the same reason the design uses
 * a raw alpha there.
 */
function FeatureBand({ section }: { readonly section: ContentData }): React.ReactElement {
  const features = list(section, 'features');
  if (features.length === 0) return <></>;
  const label = text(section, 'ctaLabel');
  const href = text(section, 'ctaHref');

  const bandEyebrow = text(section, 'bandEyebrow');
  const bandHeading = text(section, 'bandHeading');

  return (
    <Band anchor={text(section, 'anchor')}>
      {/*
        The header above the panel, centred on the page ground — the 10 September landing's
        "Why H2HSpark" over "Get More Out Of The Events You Attend". Not `SectionLeadIn`: that
        component renders the *panel's* three fields, which on `for-organizers` are the heading
        inside the gradient. Two headings, two places, and the band draws whichever it is given.
      */}
      {(bandEyebrow !== '' || bandHeading !== '') && (
        <div className="mb-9 text-center">
          {bandEyebrow !== '' && <p className="h2h-eyebrow">{bandEyebrow}</p>}
          {bandHeading !== '' && (
            <h2 className="mx-auto mt-3.5 max-w-[620px] text-3xl font-bold tracking-tight text-balance sm:text-4xl">
              {bandHeading}
            </h2>
          )}
        </div>
      )}
      <div className="grid items-center gap-12 rounded-2xl bg-spark p-8 text-white sm:p-14 lg:grid-cols-2">
        <div>
          {text(section, 'eyebrow') !== '' && (
            <p className="font-brand text-xs font-semibold uppercase tracking-[0.14em] text-white/80">
              {text(section, 'eyebrow')}
            </p>
          )}
          {/* `text-white` explicitly: the design system applies `text-foreground` to every
              heading globally, and it wins over a colour inherited from this container. The
              closing band's header records the same trap — it rendered near-black on purple. */}
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl">
            {text(section, 'heading')}
          </h2>
          {text(section, 'intro') !== '' && (
            <p className="mt-4 text-lg text-white/90">{text(section, 'intro')}</p>
          )}
          {label !== '' && href !== '' && (
            <Button
              asChild
              size="lg"
              className="mt-7 rounded-pill bg-white text-brand hover:bg-white/90"
            >
              <ContentLink href={href}>{label}</ContentLink>
            </Button>
          )}
        </div>

        <ul className="flex flex-col gap-3.5">
          {features.map((feature, index) => (
            <li
              key={index}
              className="flex items-start gap-3.5 rounded-lg bg-white/[0.14] p-[18px]"
            >
              <FeatureGlyph name={text(feature, 'icon')} />
              <div>
                <div className="font-brand text-md font-bold text-white">
                  {text(feature, 'title')}
                </div>
                <div className="text-sm text-white/85">{text(feature, 'body')}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </Band>
  );
}

/** A feature's glyph, white on the gradient. Absent or unknown draws nothing rather than a gap. */
function FeatureGlyph({ name }: { readonly name: string }): React.ReactElement | null {
  if (name === '') return null;
  const icons = Lucide as unknown as Record<string, React.ComponentType<{ className?: string }>>;
  const Icon = icons[name];
  if (Icon === undefined) return null;
  return <Icon className="h-6 w-6 shrink-0 text-white" />;
}

function CardIcon({ name }: { readonly name: string }): React.ReactElement | null {
  const icons = Lucide as unknown as Record<string, React.ComponentType<{ className?: string }>>;
  const Icon = name === '' ? undefined : icons[name];
  if (Icon === undefined) return null;
  return (
    <span className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
      <Icon className="h-6 w-6" aria-hidden />
    </span>
  );
}

/**
 * A row of figures (`CMS-018` AC-2) — the prototype's `orgStats`.
 *
 * ## Why a `dl` and why the label is first in the document
 *
 * "+41%" is not a fact; "+41% repeat attendance" is. The figure is the value and the line under it
 * is the term, which is what a description list says. `flex-col-reverse` puts the figure above its
 * label visually while the label stays first for a reader who is not looking at it — the same
 * arrangement `CaseStudyMetrics` uses, deliberately, so the two places this site draws a figure
 * draw it the same way.
 */
function StatBand({ section }: { readonly section: ContentData }): React.ReactElement {
  const stats = list(section, 'stats');
  if (stats.length === 0) return <></>;
  return (
    <Band anchor={text(section, 'anchor')}>
      <SectionLeadIn
        eyebrow={text(section, 'eyebrow')}
        heading={text(section, 'heading')}
        intro={text(section, 'intro')}
      />
      <dl className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, index) => (
          <div key={index} className="flex flex-col-reverse rounded-xl border bg-card p-6">
            <dt className="mt-2 text-sm leading-snug text-muted-foreground">
              {text(stat, 'label')}
            </dt>
            <dd className="text-3xl font-extrabold leading-none text-primary sm:text-4xl">
              {text(stat, 'value')}
            </dd>
          </div>
        ))}
      </dl>
    </Band>
  );
}

/**
 * A run of rich text — the whole of the Privacy Policy and Terms of Use pages, under their hero.
 *
 * **Directly under the hero, the prose band gives up its top padding** (TW 44221940). Every band
 * pads itself top and bottom, so a hero followed by prose stacked two bands of padding — about
 * 160px on a wide screen — between the title and the first line of the text it titles. The hero's
 * bottom padding is the gap now, which is one band's worth, the same as between any two sections.
 * `sm:pt-0` as well as `pt-0`, because `sm:py-20` is a separate class and `pt-0` alone does not
 * reach it.
 *
 * **The column is `max-w-3xl`, the width of the hero's title**, so the text lines up under the
 * heading it belongs to rather than sitting in a narrower column inside it.
 */
function Prose({
  section,
  followsHero,
}: {
  readonly section: ContentData;
  readonly followsHero: boolean;
}): React.ReactElement {
  return (
    <Band {...(followsHero ? { className: 'pt-0 sm:pt-0' } : {})}>
      <div className="mx-auto max-w-3xl">
        <RichText html={text(section, 'body')} />
      </div>
    </Band>
  );
}

/**
 * The organizer sell — a panel on the brand, not another band of it.
 *
 * The design draws this inside the footer block: a `rgba(255,255,255,.08)` card at
 * `--radius-xl`, its eyebrow in `--fuchsia-200`, and two actions. We render it as the page's last
 * section rather than inside the footer chrome — the footer is one settings entry shared by every
 * page and this band is the home page's content, so putting it there would make the chrome render
 * page content. That deviation is recorded in the diff report; **the deviation was never meant to
 * include losing the panel**, which is what happened when it shared `CallToAction` with the
 * closing band.
 */
function OrganizerSellBand({ section }: { readonly section: ContentData }): React.ReactElement {
  const label = text(section, 'ctaLabel');
  const href = text(section, 'ctaHref');
  const secondaryLabel = text(section, 'secondaryLabel');
  const secondaryHref = text(section, 'secondaryHref');

  return (
    <Band tint="brand" className="pt-0">
      <div className="flex flex-col gap-7 rounded-xl bg-white/[0.08] p-7 sm:p-8 lg:flex-row lg:items-center lg:justify-between">
        <div>
          {text(section, 'eyebrow') !== '' && (
            <p className="font-brand text-[11px] font-bold uppercase tracking-[0.06em] text-fuchsia-200">
              {text(section, 'eyebrow')}
            </p>
          )}
          {/* `text-primary-foreground` explicitly — the global heading colour wins otherwise, and
              the closing band's header records what that looked like on purple. */}
          <h3 className="mt-1 text-2xl font-bold text-primary-foreground">
            {text(section, 'headline')}
          </h3>
          {text(section, 'body') !== '' && (
            <p className="mt-2 max-w-[520px] text-md text-primary-foreground/85">
              {text(section, 'body')}
            </p>
          )}
        </div>
        {(label !== '' || secondaryLabel !== '') && (
          <div className="flex flex-none flex-wrap gap-3">
            {label !== '' && href !== '' && (
              <Button asChild className="rounded-pill bg-white text-brand hover:bg-white/90">
                <ContentLink href={href}>{label}</ContentLink>
              </Button>
            )}
            {secondaryLabel !== '' && secondaryHref !== '' && (
              <Button
                asChild
                variant="outline"
                className="rounded-pill border-white/40 bg-transparent text-primary-foreground hover:bg-white/10"
              >
                <ContentLink href={secondaryHref}>{secondaryLabel}</ContentLink>
              </Button>
            )}
          </div>
        )}
      </div>
    </Band>
  );
}

function CallToAction({ section }: { readonly section: ContentData }): React.ReactElement {
  const href = text(section, 'ctaHref');
  const label = text(section, 'ctaLabel');
  const secondaryHref = text(section, 'secondaryHref');
  const secondaryLabel = text(section, 'secondaryLabel');
  return (
    <Band tint="brand" className="text-center">
      {/* The white mark, 64px, above the headline — `assets/icon-white.png`, which the prototype
          draws here and which had never been used. Not content: it is the brand's own furniture,
          the same as the wordmark in the chrome, so it is not an authored field. */}
      <img
        src="/brand/icon-white.png"
        alt=""
        aria-hidden
        className="mx-auto mb-6 h-16 w-auto"
        width={992}
        height={717}
      />
      {/*
        `text-primary-foreground` is explicit rather than inherited, and it has to be: the design
        system sets `h1…h6 { @apply text-foreground }` globally, which **wins over the colour the
        band sets on its container**. Without it the headline renders near-black on brand purple —
        which is what the seeded call to action looked like the first time it was on a page, and
        the reason nobody had noticed is that no published page had a brand band on it before.
      */}
      <h2 className="mx-auto max-w-2xl text-2xl font-bold text-primary-foreground sm:text-3xl">
        {text(section, 'headline')}
      </h2>
      {text(section, 'body') !== '' && (
        <p className="mx-auto mt-3 max-w-xl opacity-90">{text(section, 'body')}</p>
      )}
      {/*
        **Two actions, because the prototype's band has two** (`CMS-018` AC-6): "Book a demo" in
        white, and a quieter outlined way back to the members side. The hero has carried a second
        action since the palette was written and this band did not, so transcribing the page meant
        dropping one of its two buttons — a visitor who is not ready to book a demo was left with
        no onward link at the bottom of the page.

        Each button needs **both** a label and a link. A label with no destination is a button that
        does nothing, which is worse on a call to action than no button at all.
      */}
      {((label !== '' && href !== '') || (secondaryLabel !== '' && secondaryHref !== '')) && (
        <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
          {/* **White, and a pill** — the treatment the organizer band on the same page already
              uses for its white button. `variant="secondary"` resolves to `--surface-sunken`, so
              this drew a beige 12px rectangle on `--purple-800` where §1's closing call to action
              is a white pill. Two brand bands on one page disagreeing about their own button is
              the "check the neighbours" failure `building-from-designs.md` names. Parity pass,
              2026-09-12. */}
          {label !== '' && href !== '' && (
            <Button
              asChild
              size="lg"
              className="rounded-pill bg-white text-brand hover:bg-white/90"
            >
              <ContentLink href={href}>{label}</ContentLink>
            </Button>
          )}
          {secondaryLabel !== '' && secondaryHref !== '' && (
            // Outlined in the band's own foreground rather than `variant="outline"`, which draws
            // its border and text from the page background it normally sits on and disappears
            // against brand purple.
            <Button
              asChild
              size="lg"
              variant="ghost"
              className="rounded-pill border border-primary-foreground/60 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
            >
              <ContentLink href={secondaryHref}>{secondaryLabel}</ContentLink>
            </Button>
          )}
        </div>
      )}
    </Band>
  );
}
